import { Component } from '@theme/component';
import {
  MINI_PDP_COLORWAY_CHANGE,
  fetchMiniPdpSection,
  getInitialMiniPdpSku,
  resolveSkuPropertyValue,
} from '@theme/mini-pdp-helpers';

/**
 * Attributes the section-rendering endpoint cannot know about: they come from the render
 * parameters of whichever entry point opened the drawer (hotspot, shop the look, cart upsell).
 * Carried onto the replacement form so a colorway swap keeps its attribution.
 */
const CARRIED_FORM_ATTRIBUTES = [
  'data-line-item-property-key',
  'data-line-item-property-value',
  'data-line-item-property-sku-suffix',
  'data-line-item-property-sku-template',
  'data-hotspot-handle',
  'data-hotspot-parent-product-id',
  'data-testid',
];

/**
 * Rendered per product by `snippets/line-item-properties.liquid`, so the replacement form already
 * has its own and the old product's must not leak across.
 */
const PRODUCT_OWNED_PROPERTY_NAMES = [
  'properties[_finalSale]',
  'properties[_preOrderDate]',
  'properties[Preorder]',
];

/**
 * Mini PDP Colorways
 *
 * Swaps the drawer to a sibling colorway product. The replacement markup is rendered by
 * `sections/section-rendering-mini-pdp.liquid`, so imagery, price, badges, size availability and
 * the add-to-cart form all come back correct without reimplementing that Liquid in JS.
 *
 * The whole `.mini-pdp` node is replaced rather than morphed: `mini-pdp-variant-selector` builds
 * its variant map once in `connectedCallback`, so a morph would leave it holding the old product.
 */
class MiniPdpColorways extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('mouseover', this.#handlePointerOver);
    this.addEventListener('mouseout', this.#handlePointerOut);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('mouseover', this.#handlePointerOver);
    this.removeEventListener('mouseout', this.#handlePointerOut);
    this.#abortController?.abort();
  }

  /** @param {Event} event */
  #handleChange = async (event) => {
    const input = event.target;
    const root = this.closest('.mini-pdp');
    if (!(input instanceof HTMLInputElement) || !(root instanceof HTMLElement)) return;

    this.#syncLegends();
    const variantId = resolveVariantId(input, root);

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    root.setAttribute('aria-busy', 'true');

    const detailsScrollTop = root.querySelector('.mini-pdp__details')?.scrollTop ?? 0;
    const rowScrollLeft = this.querySelector('.mini-pdp__colorways-categories')?.scrollLeft ?? 0;
    const drawerScroller = this.closest('.mini-pdp-drawer__content');
    const drawerScrollTop = drawerScroller?.scrollTop ?? 0;

    const section = await fetchMiniPdpSection(input.value, { variantId, signal });
    // Superseded by a newer selection, which now owns the loading state, or disconnected.
    if (signal.aborted) return;

    const nextRoot = section?.querySelector('.mini-pdp');
    if (!(nextRoot instanceof HTMLElement)) {
      this.#revert();
      root.removeAttribute('aria-busy');
      return;
    }

    carryOverContext(root, nextRoot);
    // Copied before insertion: components rewrite parts of their markup once connected.
    const pristineRoot = nextRoot.cloneNode(true);
    root.replaceWith(nextRoot);

    const nextDetails = nextRoot.querySelector('.mini-pdp__details');
    if (nextDetails) nextDetails.scrollTop = detailsScrollTop;
    const nextRow = nextRoot.querySelector('.mini-pdp__colorways-categories');
    if (nextRow) nextRow.scrollLeft = rowScrollLeft;
    if (drawerScroller) drawerScroller.scrollTop = drawerScrollTop;

    const nextChecked = nextRoot.querySelector('mini-pdp-colorways input[type="radio"]:checked');
    if (nextChecked instanceof HTMLElement) nextChecked.focus({ preventScroll: true });

    nextRoot.dispatchEvent(
      new CustomEvent(MINI_PDP_COLORWAY_CHANGE, { bubbles: true, detail: { pristineRoot } }),
    );
  };

  /** @param {MouseEvent} event */
  #handlePointerOver = (event) => {
    const label = findLabel(event.target);
    if (!label) return;
    const valueSpan = label.closest('fieldset')?.querySelector('[data-colorway-value]');
    if (valueSpan) valueSpan.textContent = label.dataset.colorName ?? '';
    this.#setTopLegend(label);
  };

  /** @param {MouseEvent} event */
  #handlePointerOut = (event) => {
    const label = findLabel(event.target);
    if (!label) return;
    if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;
    this.#syncLegends();
  };

  /**
   * Shows the checked colorway's name in its category legend and clears the others, the way the
   * server renders it, and points the mobile top legend back at it.
   */
  #syncLegends() {
    for (const fieldset of this.querySelectorAll('fieldset')) {
      const valueSpan = fieldset.querySelector('[data-colorway-value]');
      if (!valueSpan) continue;
      valueSpan.textContent =
        fieldset.querySelector('input:checked')?.getAttribute('data-color-name') ?? '';
    }
    this.#setTopLegend(this.querySelector('input:checked'));
  }

  /**
   * Names a swatch's category and color in the mobile top legend.
   * @param {Element | null} swatch - A swatch label or radio; both carry `data-color-name`.
   */
  #setTopLegend(swatch) {
    const fieldset = swatch?.closest('fieldset');
    const labelSpan = this.querySelector('[data-colorway-top-label]');
    const valueSpan = this.querySelector('[data-colorway-top-value]');
    if (!swatch || !fieldset || !labelSpan || !valueSpan) return;
    labelSpan.textContent = fieldset.dataset.colorwayCategory ?? '';
    valueSpan.textContent = swatch.getAttribute('data-color-name') ?? '';
  }

  /**
   * Re-checks the colorway on screen. The server marks it with the `checked` attribute, which
   * clicks never change, so it survives rapid selections that were aborted along the way.
   */
  #revert() {
    const rendered = this.querySelector('input[checked]');
    if (rendered instanceof HTMLInputElement) rendered.checked = true;
    this.#syncLegends();
  }
}

/**
 * Keeps the size the shopper already picked, when the new colorway carries it. Without a size
 * selection we send no variant so the new product renders unselected, matching the PDP.
 *
 * @param {HTMLInputElement} input - The colorway radio, carrying parallel size/variant id lists.
 * @param {HTMLElement} root
 * @returns {string | undefined}
 */
function resolveVariantId(input, root) {
  const selectedSize = root.querySelector(
    'mini-pdp-variant-selector .mini-pdp__variants-fieldset input[type="radio"]:checked'
  );
  if (!(selectedSize instanceof HTMLInputElement)) return undefined;

  const index = JSON.parse(input.dataset.sizes ?? '[]').indexOf(selectedSize.value);
  return index === -1 ? undefined : String(JSON.parse(input.dataset.variantIds ?? '[]')[index]);
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function findLabel(target) {
  const label = target instanceof Element ? target.closest('label[data-color-name]') : null;
  return label instanceof HTMLElement ? label : null;
}

/**
 * Re-applies the entry point's context to the freshly rendered markup, before it goes live.
 *
 * @param {HTMLElement} oldRoot
 * @param {HTMLElement} nextRoot
 */
function carryOverContext(oldRoot, nextRoot) {
  const oldForm = oldRoot.querySelector('product-form-component');
  const nextForm = nextRoot.querySelector('product-form-component');
  if (!(oldForm instanceof HTMLElement) || !(nextForm instanceof HTMLElement)) return;

  for (const attribute of CARRIED_FORM_ATTRIBUTES) {
    const value = oldForm.getAttribute(attribute);
    if (value != null) nextForm.setAttribute(attribute, value);
  }

  carryOverLineItemProperties(oldForm, nextForm, getInitialMiniPdpSku(nextRoot));

  // The fit note comes from a render parameter, so the fetched markup never has one.
  const fitNote = oldRoot.querySelector('.mini-pdp__fit-note');
  if (fitNote) nextForm.before(nextRoot.ownerDocument.importNode(fitNote, true));
}

/**
 * Line item properties come from the entry point: either render parameters (shop the look, cart
 * upsell, hotspots) or `applyLineItemPropertyToMiniPdp` on the Constructor path. Either way
 * the section-rendered markup has none of them, so they are copied across and any SKU-derived
 * value is recomputed for the new colorway.
 *
 * @param {HTMLElement} oldForm
 * @param {HTMLElement} nextForm
 * @param {string} sku - SKU of the variant the replacement is rendered with.
 */
function carryOverLineItemProperties(oldForm, nextForm, sku) {
  const nextFormElement = nextForm.querySelector('form');
  if (!nextFormElement) return;

  for (const input of oldForm.querySelectorAll('input[name^="properties["]')) {
    if (!(input instanceof HTMLInputElement)) continue;
    if (PRODUCT_OWNED_PROPERTY_NAMES.includes(input.name)) continue;

    const clone = nextForm.ownerDocument.importNode(input, true);
    const { skuSuffix = '', skuTemplate = '' } = clone.dataset;
    if (skuSuffix || skuTemplate) {
      clone.value = resolveSkuPropertyValue(sku, {
        value: nextForm.dataset.lineItemPropertyValue || input.value,
        skuSuffix,
        skuTemplate,
      });
    }
    nextFormElement.appendChild(clone);
  }
}

if (!customElements.get('mini-pdp-colorways')) {
  customElements.define('mini-pdp-colorways', MiniPdpColorways);
}
